import * as React from "react";
import {useEffect} from "react";
import {translate} from "../../../translate/translate";
import {
    Box, CircularProgress, Fab,
    Grid,
    Paper,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import TableActionComponent from "../../../components/table/table-action.component";
import {AssetControl} from "../../../models/basic/asset-control";
import BackButton from "../../../components/button/back-button.component";
import {useParams} from "react-router-dom";
import {AssetControlFilter, AssetControlService, TotalInvoices} from "../../../services/asset-control-service";
import {usePagination} from "../../../utils/pagination";
import {useFilters} from "../../../filters/base-filter";
import MultilevelSelectYearComponent, {
    MonthYearOption
} from "../../../components/multilevel-select-year/multilevel-select-year.component";
import {Search} from "@mui/icons-material";
import {formatNumber} from "../../../utils/format-number";
import {orderByFn} from "../../../utils/order-by";


const AssetControlInvoiceComponent = () => {
    const params = useParams();
    const id = params.id;
    const service = new AssetControlService();

    const [invoiceList, setPagination] =
        React.useState<PaginatedResult<AssetControl>>();
    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<AssetControl>(invoiceList);

    const { filters, updateFilterKeepingParam } = useFilters<AssetControlFilter>(
        { id_project_summary: id, date: "", ordering: "" },
        ["date", "ordering"]
    );

    const expandById = ["project", "client", "lob"];
    const expandAll = ["part_number"];
    const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');

    const [detailDataSource, setDetailDataSource] = React.useState<AssetControl>();

    const [loading, setLoading] = React.useState(false);
    const [options, setOptions] = React.useState<MonthYearOption[]>([]);
    const [totalInvoices, setTotalInvoices] = React.useState<TotalInvoices>(
        {
            sum_total: 0,
            sum_amtz_qtd: 0,
        }
    );

    useEffect(() => {
        loadInvoiceDates();
        search();
        detail();
    }, []);

    const detail = () => {
        service.readById(id!, {}, expandById).then(response => {
            setDetailDataSource(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    const loadInvoiceDates = () => {
        if (!id) return;
        service.get_invoice_dates(id).then(response => {
            setOptions(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const getTotalInvoices = () => {
        service.getTotalInvoices(filters).then(response => {
            setTotalInvoices(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        })
    }

    const search = (restartIndex = false) => {
        setLoading(true);
        service.listInvoices(filters, expandAll, pagination).then(response => {
            setPagination(response.data);
            getTotalInvoices();
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });
        restartIndex && resetPage();
    };

    const updateDate = (value: string[]) => {
        updateFilterKeepingParam("date", value.join(','));
    }


    useEffect(() => {
        search();
    }, [pagination, filters.ordering]);


    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <BackButton />
                <Typography variant={"h5"}>
                    {translate.t("invoices")}
                </Typography>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Grid container={true} spacing={2}>
                            <Grid size={2} display="flex" flexDirection="row" gap={3}>
                                <Typography variant={"body1"} fontWeight="bold">
                                    Platform:
                                </Typography>
                                <Typography variant={"body1"}>
                                    {detailDataSource?.project.name || "---"}
                                </Typography>
                            </Grid>
                            <Grid size={2} display="flex" flexDirection="row" gap={3}>
                                <Typography variant={"body1"} fontWeight="bold">
                                    LOB:
                                </Typography>
                                <Typography variant={"body1"}>
                                    {detailDataSource?.lob?.name || "---"}
                                </Typography>
                            </Grid>
                            <Grid size={2} display="flex" flexDirection="row" gap={3}>
                                <Typography variant={"body1"} fontWeight="bold">
                                    Total:
                                </Typography>
                                <Typography variant={"body1"}>
                                    {formatNumber(totalInvoices.sum_total, true)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>

            <Grid size={12} pt={2}>
                <Paper variant="outlined">
                    <Box alignItems="center" display="flex" justifyContent="space-between" p={2}>
                        <Grid alignItems="center" container spacing={2} direction={"row"}
                              width="80%">
                            <Grid size={3}>
                                <MultilevelSelectYearComponent
                                    options={options}
                                    changeValues={updateDate}
                                    values={filters.date ? filters.date.split(",") : []}
                                />
                            </Grid>
                            <Grid size={1}>
                                <Fab
                                    size="small"
                                    type="submit"
                                    onClick={() => search(true)}
                                    color="secondary">
                                    <Search/>
                                </Fab>
                            </Grid>
                        </Grid>
                        <Grid alignItems="center" container justifyContent="flex-end" spacing={3}
                              width="20%">
                            <Grid >
                                {loading && <CircularProgress size="30px"/>}
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSource}
                    displayedColumnsShow={["Document", "Date", "Amtz. QTY", "NRE", "Total", "PartNumber", "Description"]}
                    displayedColumns={["nf", "invoice_date", "amtz_qtd", "nre","total", "part_number.part_number", "description"]}
                    numberColumns={["amtz_qtd", "total", "nre"]}
                    currencyColumns={["total", "nre"]}
                    integerColumns={["amtz_qtd"]}
                    orderByColumns={["invoice_date"]}
                    orderBy={filters.ordering}
                    orderByFn={(column: string, orderDirection: any) => {
                        orderByFn(column, orderDirection, filters.ordering || '', setOrderDirection, updateFilterKeepingParam);
                    }}
                    orderDirection={orderDirection}
                    dateColumns={["invoice_date"]}
                    actionsComponent={TableActionComponent}
                    count={invoiceList?.count}
                    next={invoiceList?.next}
                    previous={invoiceList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    loading={loading}
                    columnStyles={{1: "10%", 2: "10%", 3: "10%", 4: "10%", 5: "10%", 6: "10%", 7: "10%", 8: "30%"}}
                />
            </Paper>
        </section>
    );
};

export default AssetControlInvoiceComponent;
